import React, { useEffect, useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import {
  getHeroContent,
  removeHeroAssets,
  saveHeroContent,
  uploadHeroAsset,
  validateHeroAsset,
} from '../../../services/heroContentService';

const DEFAULT_TABS = [
  {
    id: 'form-builder',
    label: 'Form Builder',
    icon: 'LayoutTemplate',
    headline: 'Build any form in minutes',
    description: 'Drag-and-drop fields, conditional logic, multi-step flows, and custom themes — no code needed.',
    mediaType: 'image',
    mediaUrl: '',
    mediaAlt: '',
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: 'BarChart3',
    headline: "See exactly what\'s working",
    description: 'Real-time submission tracking, conversion funnels, device breakdowns, and geographic insights.',
    mediaType: 'image',
    mediaUrl: '',
    mediaAlt: '',
  },
  {
    id: 'payments',
    label: 'Payments',
    icon: 'CreditCard',
    headline: 'Collect payments inside your forms',
    description: 'Accept one-time or recurring payments, set pricing rules, and manage transactions — all in one place.',
    mediaType: 'image',
    mediaUrl: '',
    mediaAlt: '',
  },
  {
    id: 'submissions',
    label: 'Submissions',
    icon: 'Inbox',
    headline: 'Manage every response in one inbox',
    description: 'Filter, label, export, and act on submissions. Get notified instantly when new responses arrive.',
    mediaType: 'image',
    mediaUrl: '',
    mediaAlt: '',
  },
];

const ICON_OPTIONS = [
  'LayoutTemplate', 'BarChart3', 'CreditCard', 'Zap', 'Inbox', 'FileText',
  'Settings', 'Users', 'Shield', 'Star', 'Globe', 'Mail', 'Bell', 'Lock',
  'Database', 'Code', 'Layers', 'Package', 'Cpu', 'Activity',
];

const BG_TYPES = [
  { id: 'gradient', label: 'Gradient' },
  { id: 'solid', label: 'Solid Color' },
  { id: 'image', label: 'Image' },
];

const DEFAULT_BACKGROUND = {
  type: 'gradient',
  gradientFrom: '#ffffff',
  gradientTo: '#007876',
  gradientToOpacity: 50,
  gradientDirection: 'to bottom',
  solidColor: '#ffffff',
  mediaUrl: '',
};

const safeText = (value, fallback = '', maxLength = 1000) => (
  typeof value === 'string' ? value?.slice(0, maxLength) : fallback
);

const normalizeTab = (tab, index) => ({
  id: /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,79}$/.test(tab?.id || '') ? tab?.id : `hero-section-${index + 1}`,
  label: safeText(tab?.label, `Section ${index + 1}`, 80),
  icon: safeText(tab?.icon, 'Star', 80),
  headline: safeText(tab?.headline, '', 160),
  description: safeText(tab?.description, '', 1000),
  mediaType: tab?.mediaType === 'video' ? 'video' : 'image',
  mediaUrl: safeText(tab?.mediaUrl || tab?.image, '', 2000),
  mediaPath: safeText(tab?.mediaPath, '', 500),
  mediaAlt: safeText(tab?.mediaAlt, '', 300),
});

const normalizeBackground = (background) => {
  const candidate = background && typeof background === 'object' ? background : {};
  const directions = ['to bottom', 'to top', 'to right', 'to left', 'to bottom right', 'to bottom left'];
  return {
    type: ['gradient', 'solid', 'image']?.includes(candidate?.type) ? candidate?.type : DEFAULT_BACKGROUND?.type,
    gradientFrom: /^#[0-9a-fA-F]{6}$/.test(candidate?.gradientFrom || '') ? candidate?.gradientFrom : DEFAULT_BACKGROUND?.gradientFrom,
    gradientTo: /^#[0-9a-fA-F]{6}$/.test(candidate?.gradientTo || '') ? candidate?.gradientTo : DEFAULT_BACKGROUND?.gradientTo,
    gradientToOpacity: Number.isFinite(candidate?.gradientToOpacity)
      ? Math.min(100, Math.max(0, candidate?.gradientToOpacity))
      : DEFAULT_BACKGROUND?.gradientToOpacity,
    gradientDirection: directions?.includes(candidate?.gradientDirection)
      ? candidate?.gradientDirection
      : DEFAULT_BACKGROUND?.gradientDirection,
    solidColor: /^#[0-9a-fA-F]{6}$/.test(candidate?.solidColor || '') ? candidate?.solidColor : DEFAULT_BACKGROUND?.solidColor,
    mediaUrl: safeText(candidate?.mediaUrl, '', 2000),
    mediaPath: safeText(candidate?.mediaPath, '', 500),
  };
};

const HeroContentManager = () => {
  const [tabs, setTabs] = useState(DEFAULT_TABS);
  const [selectedTabId, setSelectedTabId] = useState(DEFAULT_TABS?.[0]?.id);
  const [bgType, setBgType] = useState('gradient');
  const [gradientFrom, setGradientFrom] = useState('#ffffff');
  const [gradientTo, setGradientTo] = useState('#007876');
  const [gradientToOpacity, setGradientToOpacity] = useState(50);
  const [gradientDirection, setGradientDirection] = useState('to bottom');
  const [solidColor, setSolidColor] = useState('#ffffff');
  const [bgImageUrl, setBgImageUrl] = useState('');
  const [bgImagePath, setBgImagePath] = useState('');
  const [bgImageFile, setBgImageFile] = useState(null);
  const [saved, setSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [mediaError, setMediaError] = useState('');
  const [pendingTabFiles, setPendingTabFiles] = useState({});
  const [showAddTab, setShowAddTab] = useState(false);
  const [newTab, setNewTab] = useState({
    label: '',
    icon: 'Star',
    headline: '',
    description: '',
    mediaType: 'image',
    mediaUrl: '',
    mediaAlt: '',
  });
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [editIconPickerOpen, setEditIconPickerOpen] = useState(false);
  const bgImageInputRef = useRef(null);
  const tabImageInputRef = useRef(null);

  const selectedTab = tabs?.find(t => t?.id === selectedTabId);

  useEffect(() => {
    let active = true;

    const loadContent = async () => {
      setIsLoading(true);
      setSaveError('');
      try {
        const content = await getHeroContent();
        if (!active || !content) return;

        const loadedTabs = Array.isArray(content?.tabs) && content?.tabs?.length
          ? content?.tabs?.map(normalizeTab)
          : DEFAULT_TABS;
        const background = normalizeBackground(content?.background);

        setTabs(loadedTabs);
        setSelectedTabId(loadedTabs?.[0]?.id || null);
        setBgType(background?.type);
        setGradientFrom(background?.gradientFrom);
        setGradientTo(background?.gradientTo);
        setGradientToOpacity(background?.gradientToOpacity);
        setGradientDirection(background?.gradientDirection);
        setSolidColor(background?.solidColor);
        setBgImageUrl(background?.mediaUrl || '');
        setBgImagePath(background?.mediaPath || '');
      } catch (error) {
        if (active) setSaveError(error?.message || 'Could not load the saved hero content.');
      } finally {
        if (active) setIsLoading(false);
      }
    };

    loadContent();
    return () => {
      active = false;
    };
  }, []);

  const updateTab = (field, value) => {
    setTabs(prev => prev?.map(t => t?.id === selectedTabId ? { ...t, [field]: value } : t));
    setSaved(false);
    setSaveError('');
  };

  const deleteTab = (id) => {
    const remaining = tabs?.filter(t => t?.id !== id);
    setTabs(remaining);
    if (selectedTabId === id) setSelectedTabId(remaining?.[0]?.id || null);
    setPendingTabFiles(prev => {
      const next = { ...prev };
      delete next?.[id];
      return next;
    });
    setSaved(false);
  };

  const addTab = () => {
    if (!newTab?.label?.trim()) return;
    const id = newTab?.label?.toLowerCase()?.replace(/\s+/g, '-') + '-' + Date.now();
    setTabs(prev => [...prev, { ...newTab, id }]);
    setSelectedTabId(id);
    setNewTab({ label: '', icon: 'Star', headline: '', description: '', mediaType: 'image', mediaUrl: '', mediaAlt: '' });
    setShowAddTab(false);
    setSaved(false);
  };

  const handleBgImageFile = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    try {
      const mediaType = validateHeroAsset(file);
      if (mediaType !== 'image') throw new Error('The hero background must be an image.');
      setMediaError('');
      setBgImageFile(file);
      setBgImageUrl(URL.createObjectURL(file));
      setSaved(false);
    } catch (error) {
      setMediaError(error?.message || 'Could not use that background image.');
      e.target.value = '';
    }
  };

  const handleTabMediaFile = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    try {
      const mediaType = validateHeroAsset(file);
      setMediaError('');
      setPendingTabFiles(prev => ({ ...prev, [selectedTabId]: file }));
      setTabs(prev => prev?.map(tab => tab?.id === selectedTabId
        ? { ...tab, mediaType, mediaUrl: URL.createObjectURL(file) }
        : tab));
      setSaved(false);
    } catch (error) {
      setMediaError(error?.message || 'Could not use that media file.');
      e.target.value = '';
    }
  };

  const moveTab = (id, direction) => {
    setTabs(prev => {
      const currentIndex = prev?.findIndex(tab => tab?.id === id);
      const nextIndex = currentIndex + direction;
      if (currentIndex < 0 || nextIndex < 0 || nextIndex >= prev?.length) return prev;
      const next = [...prev];
      [next[currentIndex], next[nextIndex]] = [next[nextIndex], next[currentIndex]];
      return next;
    });
    setSaved(false);
  };

  const clearSelectedTabMedia = () => {
    setPendingTabFiles(prev => {
      const next = { ...prev };
      delete next?.[selectedTabId];
      return next;
    });
    setTabs(prev => prev?.map(tab => tab?.id === selectedTabId
      ? { ...tab, mediaUrl: '', mediaPath: '' }
      : tab));
    setSaved(false);
    setSaveError('');
  };

  const updateSelectedTabMediaUrl = (value) => {
    setPendingTabFiles(prev => {
      const next = { ...prev };
      delete next?.[selectedTabId];
      return next;
    });
    setTabs(prev => prev?.map(tab => tab?.id === selectedTabId
      ? { ...tab, mediaUrl: value, mediaPath: '' }
      : tab));
    setSaved(false);
    setSaveError('');
  };

  const handleSave = async () => {
    if (!tabs?.length) {
      setSaveError('Add at least one hero section before saving.');
      return;
    }

    setIsSaving(true);
    setSaved(false);
    setSaveError('');
    setMediaError('');
    const uploadedPaths = [];

    try {
      const previousManagedPaths = [
        ...tabs?.map(tab => tab?.mediaPath),
        bgImagePath,
      ]?.filter(Boolean);
      const resolvedTabs = [];

      for (const tab of tabs) {
        const pendingFile = pendingTabFiles?.[tab?.id];
        if (!pendingFile) {
          resolvedTabs?.push(normalizeTab(tab, resolvedTabs?.length));
          continue;
        }
        const uploaded = await uploadHeroAsset(pendingFile, tab?.id);
        uploadedPaths?.push(uploaded?.filePath);
        resolvedTabs?.push({
          ...normalizeTab(tab, resolvedTabs?.length),
          mediaType: uploaded?.mediaType,
          mediaUrl: uploaded?.mediaUrl,
          mediaPath: uploaded?.filePath,
        });
      }

      let resolvedBackgroundUrl = bgImageUrl?.startsWith('blob:') ? '' : bgImageUrl;
      let resolvedBackgroundPath = bgImagePath;
      if (bgImageFile) {
        const uploaded = await uploadHeroAsset(bgImageFile, 'background');
        uploadedPaths?.push(uploaded?.filePath);
        resolvedBackgroundUrl = uploaded?.mediaUrl;
        resolvedBackgroundPath = uploaded?.filePath;
      }

      const config = {
        version: 1,
        tabs: resolvedTabs,
        background: {
          type: bgType,
          gradientFrom,
          gradientTo,
          gradientToOpacity,
          gradientDirection,
          solidColor,
          mediaUrl: resolvedBackgroundUrl,
          mediaPath: resolvedBackgroundPath,
        },
      };

      await saveHeroContent(config);
      const currentManagedPaths = new Set([
        ...resolvedTabs?.map(tab => tab?.mediaPath),
        resolvedBackgroundPath,
      ]?.filter(Boolean));
      const obsoletePaths = previousManagedPaths?.filter(path => !currentManagedPaths?.has(path));
      if (obsoletePaths?.length) {
        try {
          await removeHeroAssets(obsoletePaths);
        } catch (cleanupError) {
          console.error('Saved hero content, but failed to remove replaced media:', cleanupError);
          setMediaError('Changes were saved, but an old media file could not be removed.');
        }
      }
      setTabs(resolvedTabs);
      setBgImageUrl(resolvedBackgroundUrl);
      setBgImagePath(resolvedBackgroundPath);
      setBgImageFile(null);
      setPendingTabFiles({});
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      if (uploadedPaths?.length) {
        try {
          await removeHeroAssets(uploadedPaths);
        } catch (cleanupError) {
          console.error('Failed to clean up unsaved hero media:', cleanupError);
        }
      }
      setSaveError(error?.message || 'Hero changes could not be saved. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const hexToRgba = (hex, opacity) => {
    const r = parseInt(hex?.slice(1, 3), 16);
    const g = parseInt(hex?.slice(3, 5), 16);
    const b = parseInt(hex?.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${opacity / 100})`;
  };

  const previewBg = bgType === 'gradient'
    ? `linear-gradient(${gradientDirection}, ${gradientFrom} 0%, ${hexToRgba(gradientTo, gradientToOpacity)} 100%)`
    : bgType === 'solid'
    ? solidColor
    : bgImageUrl
    ? `url(${bgImageUrl}) center/cover no-repeat`
    : '#f9fafb';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-heading font-semibold text-foreground">Hero Section Manager</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Edit hero tabs, descriptions, images, and background style</p>
        </div>
        <button
          onClick={handleSave}
          disabled={isLoading || isSaving}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
            saved
              ? 'bg-green-100 text-green-700 border border-green-200'
              : 'bg-primary text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60'
          }`}
        >
          <Icon name={saved ? 'CheckCircle' : isSaving ? 'Loader2' : 'Save'} size={15} className={isSaving ? 'animate-spin' : ''} />
          {saved ? 'Saved!' : isSaving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
          <Icon name="Loader2" size={15} className="animate-spin text-primary" />
          Loading saved hero content…
        </div>
      )}
      {saveError && (
        <div role="alert" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <Icon name="AlertCircle" size={15} className="mt-0.5 flex-shrink-0" />
          <span>{saveError}</span>
        </div>
      )}
      {mediaError && (
        <div role="alert" className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <Icon name="AlertTriangle" size={15} className="mt-0.5 flex-shrink-0" />
          <span>{mediaError}</span>
        </div>
      )}

      <fieldset disabled={isLoading || isSaving} className="grid grid-cols-1 xl:grid-cols-5 gap-6 disabled:opacity-70">
        {/* LEFT: Tab List + Editor */}
        <div className="xl:col-span-3 space-y-5">

          {/* Tab List */}
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Icon name="Layers" size={15} className="text-primary" />
                Hero Sections
              </h3>
              <button
                onClick={() => setShowAddTab(v => !v)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg text-xs font-medium transition-colors"
              >
                <Icon name="Plus" size={13} />
                Add Section
              </button>
            </div>

            {/* Add Tab Form */}
            {showAddTab && (
              <div className="mb-4 p-4 bg-muted/40 rounded-lg border border-border space-y-3">
                <p className="text-xs font-medium text-foreground">New Hero Section</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Tab Label *</label>
                    <input
                      type="text"
                      value={newTab?.label}
                      onChange={e => setNewTab(p => ({ ...p, label: e?.target?.value }))}
                      placeholder="e.g. Integrations"
                      className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Icon</label>
                    <div className="relative">
                      <button
                        onClick={() => setIconPickerOpen(v => !v)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground hover:border-primary/50 transition-colors"
                      >
                        <Icon name={newTab?.icon} size={14} className="text-primary" />
                        <span>{newTab?.icon}</span>
                        <Icon name="ChevronDown" size={12} className="ml-auto text-muted-foreground" />
                      </button>
                      {iconPickerOpen && (
                        <div className="absolute top-full left-0 mt-1 z-20 bg-card border border-border rounded-lg shadow-lg p-2 grid grid-cols-5 gap-1 w-48">
                          {ICON_OPTIONS?.map(ic => (
                            <button
                              key={ic}
                              onClick={() => { setNewTab(p => ({ ...p, icon: ic })); setIconPickerOpen(false); }}
                              className={`p-1.5 rounded hover:bg-muted flex items-center justify-center ${newTab?.icon === ic ? 'bg-primary/10 text-primary' : 'text-muted-foreground'}`}
                              title={ic}
                            >
                              <Icon name={ic} size={14} />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Headline</label>
                  <input
                    type="text"
                    value={newTab?.headline}
                    onChange={e => setNewTab(p => ({ ...p, headline: e?.target?.value }))}
                    placeholder="Short headline for this tab"
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Description</label>
                  <textarea
                    value={newTab?.description}
                    onChange={e => setNewTab(p => ({ ...p, description: e?.target?.value }))}
                    placeholder="Describe this feature..."
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                  />
                </div>
                <div className="flex gap-2">
                  <button onClick={addTab} className="flex-1 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors">
                    Add Section
                  </button>
                  <button onClick={() => setShowAddTab(false)} className="px-4 py-2 border border-border text-sm text-muted-foreground rounded-lg hover:bg-muted transition-colors">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Tab Items */}
            <div className="space-y-2">
              {tabs?.map((tab, idx) => (
                <div
                  key={tab?.id}
                  onClick={() => setSelectedTabId(tab?.id)}
                  className={`flex items-center gap-3 px-3 py-3 rounded-lg border cursor-pointer transition-all ${
                    selectedTabId === tab?.id
                      ? 'border-primary/40 bg-primary/5' :'border-border hover:border-primary/20 hover:bg-muted/30'
                  }`}
                >
                  <div className={`p-1.5 rounded-md ${selectedTabId === tab?.id ? 'bg-primary/15' : 'bg-muted'}`}>
                    <Icon name={tab?.icon} size={14} className={selectedTabId === tab?.id ? 'text-primary' : 'text-muted-foreground'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{tab?.label}</p>
                    <p className="text-xs text-muted-foreground truncate">{tab?.headline || 'No headline set'}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className="text-xs text-muted-foreground">#{idx + 1}</span>
                    <button
                      onClick={e => { e?.stopPropagation(); moveTab(tab?.id, -1); }}
                      disabled={idx === 0}
                      className="p-1 rounded text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30 transition-colors"
                      aria-label={`Move ${tab?.label} up`}
                    >
                      <Icon name="ChevronUp" size={13} />
                    </button>
                    <button
                      onClick={e => { e?.stopPropagation(); moveTab(tab?.id, 1); }}
                      disabled={idx === tabs?.length - 1}
                      className="p-1 rounded text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30 transition-colors"
                      aria-label={`Move ${tab?.label} down`}
                    >
                      <Icon name="ChevronDown" size={13} />
                    </button>
                    {tabs?.length > 1 && (
                      <button
                        onClick={e => { e?.stopPropagation(); deleteTab(tab?.id); }}
                        className="p-1 rounded hover:bg-red-50 hover:text-red-500 text-muted-foreground transition-colors ml-1"
                      >
                        <Icon name="Trash2" size={13} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tab Editor */}
          {selectedTab && (
            <div className="bg-card border border-border rounded-xl p-5 space-y-4">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Icon name="Pencil" size={15} className="text-primary" />
                Edit Tab: <span className="text-primary">{selectedTab?.label}</span>
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">Tab Label</label>
                  <input
                    type="text"
                    value={selectedTab?.label}
                    onChange={e => updateTab('label', e?.target?.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">Icon</label>
                  <div className="relative">
                    <button
                      onClick={() => setEditIconPickerOpen(v => !v)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground hover:border-primary/50 transition-colors"
                    >
                      <Icon name={selectedTab?.icon} size={14} className="text-primary" />
                      <span>{selectedTab?.icon}</span>
                      <Icon name="ChevronDown" size={12} className="ml-auto text-muted-foreground" />
                    </button>
                    {editIconPickerOpen && (
                      <div className="absolute top-full left-0 mt-1 z-20 bg-card border border-border rounded-lg shadow-lg p-2 grid grid-cols-5 gap-1 w-48">
                        {ICON_OPTIONS?.map(ic => (
                          <button
                            key={ic}
                            onClick={() => { updateTab('icon', ic); setEditIconPickerOpen(false); }}
                            className={`p-1.5 rounded hover:bg-muted flex items-center justify-center ${selectedTab?.icon === ic ? 'bg-primary/10 text-primary' : 'text-muted-foreground'}`}
                            title={ic}
                          >
                            <Icon name={ic} size={14} />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Headline</label>
                <input
                  type="text"
                  value={selectedTab?.headline}
                  onChange={e => updateTab('headline', e?.target?.value)}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Description</label>
                <textarea
                  value={selectedTab?.description}
                  onChange={e => updateTab('description', e?.target?.value)}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                />
              </div>

              {/* Section Media */}
              <div>
                <div className="flex items-center justify-between gap-3 mb-1.5">
                  <label className="block text-xs font-medium text-muted-foreground">Section image or video (optional)</label>
                  <select
                    value={selectedTab?.mediaType}
                    onChange={e => updateTab('mediaType', e?.target?.value)}
                    className="px-2 py-1 text-xs border border-border rounded-md bg-background text-foreground"
                  >
                    <option value="image">Image</option>
                    <option value="video">Video</option>
                  </select>
                </div>
                <div className="flex items-start gap-3">
                  {selectedTab?.mediaUrl ? (
                    <div className="relative w-28 h-20 rounded-lg overflow-hidden border border-border bg-black/5 flex-shrink-0">
                      {selectedTab?.mediaType === 'video' ? (
                        <video
                          src={selectedTab?.mediaUrl}
                          autoPlay
                          muted
                          loop
                          playsInline
                          preload="metadata"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <img src={selectedTab?.mediaUrl} alt={selectedTab?.mediaAlt || 'Hero section preview'} className="w-full h-full object-cover" />
                      )}
                      <button
                        onClick={clearSelectedTabMedia}
                        className="absolute top-1 right-1 p-0.5 bg-black/60 rounded text-white hover:bg-black/80"
                        aria-label="Remove section media"
                      >
                        <Icon name="X" size={10} />
                      </button>
                    </div>
                  ) : (
                    <div className="w-24 h-16 rounded-lg border-2 border-dashed border-border bg-muted/30 flex items-center justify-center flex-shrink-0">
                      <Icon name="ImagePlus" size={18} className="text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 space-y-2">
                    <button
                      onClick={() => tabImageInputRef?.current?.click()}
                      className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg text-xs text-muted-foreground hover:border-primary/50 hover:text-primary transition-colors"
                    >
                      <Icon name="Upload" size={13} />
                      Upload image or video
                    </button>
                    <input
                      type="text"
                      value={selectedTab?.mediaUrl}
                      onChange={e => updateSelectedTabMediaUrl(e?.target?.value)}
                      placeholder="Or paste an image/video URL…"
                      className="w-full px-3 py-2 text-xs border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                    <input
                      type="text"
                      value={selectedTab?.mediaAlt}
                      onChange={e => updateTab('mediaAlt', e?.target?.value)}
                      placeholder="Describe the media for accessibility…"
                      className="w-full px-3 py-2 text-xs border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                    <input
                      ref={tabImageInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif,image/avif,video/mp4,video/webm,video/quicktime"
                      className="hidden"
                      onChange={handleTabMediaFile}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: Background Settings + Preview */}
        <div className="xl:col-span-2 space-y-5">

          {/* Background Settings */}
          <div className="bg-card border border-border rounded-xl p-5 space-y-4">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Icon name="Palette" size={15} className="text-primary" />
              Background Style
            </h3>

            {/* Type Selector */}
            <div className="flex gap-1 bg-muted/40 rounded-lg p-1">
              {BG_TYPES?.map(bt => (
                <button
                  key={bt?.id}
                  onClick={() => setBgType(bt?.id)}
                  className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${
                    bgType === bt?.id
                      ? 'bg-card text-foreground shadow-sm border border-border'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {bt?.label}
                </button>
              ))}
            </div>

            {/* Gradient Controls */}
            {bgType === 'gradient' && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1.5">From Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={gradientFrom}
                        onChange={e => setGradientFrom(e?.target?.value)}
                        className="w-8 h-8 rounded border border-border cursor-pointer"
                      />
                      <input
                        type="text"
                        value={gradientFrom}
                        onChange={e => setGradientFrom(e?.target?.value)}
                        className="flex-1 px-2 py-1.5 text-xs border border-border rounded-lg bg-background text-foreground font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1.5">To Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={gradientTo}
                        onChange={e => setGradientTo(e?.target?.value)}
                        className="w-8 h-8 rounded border border-border cursor-pointer"
                      />
                      <input
                        type="text"
                        value={gradientTo}
                        onChange={e => setGradientTo(e?.target?.value)}
                        className="flex-1 px-2 py-1.5 text-xs border border-border rounded-lg bg-background text-foreground font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1.5">
                    To Color Opacity: <span className="font-medium text-foreground">{gradientToOpacity}%</span>
                  </label>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={gradientToOpacity}
                    onChange={e => setGradientToOpacity(Number(e?.target?.value))}
                    className="w-full accent-primary"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1.5">Direction</label>
                  <select
                    value={gradientDirection}
                    onChange={e => setGradientDirection(e?.target?.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    <option value="to bottom">Top → Bottom</option>
                    <option value="to top">Bottom → Top</option>
                    <option value="to right">Left → Right</option>
                    <option value="to left">Right → Left</option>
                    <option value="to bottom right">Diagonal ↘</option>
                    <option value="to bottom left">Diagonal ↙</option>
                  </select>
                </div>
              </div>
            )}

            {/* Solid Color Controls */}
            {bgType === 'solid' && (
              <div>
                <label className="block text-xs text-muted-foreground mb-1.5">Background Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={solidColor}
                    onChange={e => setSolidColor(e?.target?.value)}
                    className="w-10 h-10 rounded border border-border cursor-pointer"
                  />
                  <input
                    type="text"
                    value={solidColor}
                    onChange={e => setSolidColor(e?.target?.value)}
                    className="flex-1 px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
              </div>
            )}

            {/* Image Controls */}
            {bgType === 'image' && (
              <div className="space-y-3">
                {bgImageUrl && (
                  <div className="relative w-full h-20 rounded-lg overflow-hidden border border-border">
                    <img src={bgImageUrl} alt="Background preview" className="w-full h-full object-cover" />
                    <button
                      onClick={() => { setBgImageUrl(''); setBgImagePath(''); setBgImageFile(null); setSaved(false); }}
                      className="absolute top-1.5 right-1.5 p-1 bg-black/60 rounded text-white hover:bg-black/80"
                    >
                      <Icon name="X" size={11} />
                    </button>
                  </div>
                )}
                <button
                  onClick={() => bgImageInputRef?.current?.click()}
                  className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-border rounded-lg text-sm text-muted-foreground hover:border-primary/50 hover:text-primary transition-colors"
                >
                  <Icon name="Upload" size={15} />
                  Upload background image
                </button>
                <input
                  type="text"
                  value={bgImageUrl}
                  onChange={e => {
                    setBgImageUrl(e?.target?.value);
                    setBgImagePath('');
                    setBgImageFile(null);
                    setSaved(false);
                    setSaveError('');
                  }}
                  placeholder="Or paste image URL..."
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
                <input ref={bgImageInputRef} type="file" accept="image/*" className="hidden" onChange={handleBgImageFile} />
              </div>
            )}
          </div>

          {/* Live Preview */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-3">
              <Icon name="Eye" size={15} className="text-primary" />
              Background Preview
            </h3>
            <div
              className="w-full h-36 rounded-xl border border-border overflow-hidden relative"
              style={{ background: previewBg }}
            >
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4">
                <div className="text-center">
                  <p className="text-xs font-semibold text-gray-800 opacity-80">Hero Section Preview</p>
                  <p className="text-[10px] text-gray-600 opacity-60 mt-0.5">Background style applied</p>
                </div>
                <div className="flex gap-1.5 mt-1">
                  {tabs?.slice(0, 4)?.map(tab => (
                    <div
                      key={tab?.id}
                      className={`px-2 py-1 rounded-full text-[9px] font-medium border ${
                        tab?.id === selectedTabId
                          ? 'bg-[#007876] text-white border-[#007876]'
                          : 'bg-white/70 text-gray-600 border-gray-200'
                      }`}
                    >
                      {tab?.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              This preview shows how the hero background will appear
            </p>
          </div>

          {/* CSS Output */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-3">
              <Icon name="Code" size={15} className="text-primary" />
              Generated CSS
            </h3>
            <div className="bg-muted/40 rounded-lg p-3 font-mono text-xs text-muted-foreground break-all">
              {bgType === 'gradient' && (
                <span>
                  background: linear-gradient({gradientDirection}, {gradientFrom} 0%, {hexToRgba(gradientTo, gradientToOpacity)} 100%);
                </span>
              )}
              {bgType === 'solid' && (
                <span>background-color: {solidColor};</span>
              )}
              {bgType === 'image' && bgImageUrl && (
                <span>background: url("{bgImageUrl}") center/cover no-repeat;</span>
              )}
              {bgType === 'image' && !bgImageUrl && (
                <span className="text-muted-foreground/50">No image selected yet</span>
              )}
            </div>
          </div>
        </div>
      </fieldset>
    </div>
  );
};

export default HeroContentManager;
